/**
 * @fileoverview Layout principal da aplicação autenticada.
 *
 * Orquestra sidebar, top bar, conteúdo principal e navegação mobile.
 * Gerencia:
 * - Sincronização de dados (DbSync, InsightNotifier)
 * - Command Palette (Cmd+K) e atalhos de teclado (Cmd+1-5)
 * - Progresso de sync com barra de carregamento
 * - Scroll-to-top automático na troca de página
 * - Redirect para /workspaces se nenhum projeto estiver selecionado
 */
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { useAnalyticsTracker, setupGlobalErrorTracking } from '@/hooks/useAnalyticsTracker';
import { useNotifications } from '@/hooks/useNotifications';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { useShallow } from 'zustand/shallow';
import { useProfile } from '@/modules/crm/hooks/useSupabaseData';
import DbSync from '@/components/DbSync';
import InsightNotifier from '@/components/InsightNotifier';
import CommandPalette from '@/components/CommandPalette';
import KeyboardShortcutsModal from '@/components/KeyboardShortcutsModal';
import SaveIndicator from '@/components/SaveIndicator';
import GlobalSearchDialog from '@/components/GlobalSearchDialog';
import SkipLink from '@/components/SkipLink';
import { supabase } from '@/integrations/supabase/client';
import { useAuditLog } from '@/hooks/useAuditLog';
import { useIsMobile } from '@/hooks/use-mobile';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { AppTopBar } from '@/components/layout/AppTopBar';
import { STORAGE_KEYS } from '@/lib/constants';
// Removed framer-motion — replaced with CSS animation (animate-in fade-in)
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
// Icons used by child components are imported in their respective files
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { LayoutDashboard, Menu, Kanban, Users, Activity } from 'lucide-react';

const OnboardingWizard = lazy(() => import('@/components/OnboardingWizard'));
const PlanUsageBannerLazy = lazy(() => import('@/components/PlanUsageBanner').then(m => ({ default: m.PlanUsageBanner })));

// Removed module-level guard — now properly re-registers on project change

function AppLayout() {
  const { theme, toggleTheme, activeProjectId, activeProjectType, userProfile, syncProgress } = useStore(useShallow((s) => ({
    theme: s.theme,
    toggleTheme: s.toggleTheme,
    activeProjectId: s.activeProjectId,
    activeProjectType: s.activeProjectType,
    userProfile: s.userProfile,
    syncProgress: s.syncProgress,
  })));
  const { profile: dbProfile } = useProfile();
  const { log: auditLog } = useAuditLog();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { trackFeature } = useAnalyticsTracker();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED) === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const prevPathRef = useRef(location.pathname);

  // Scroll main content to top only when navigating to a different page
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      mainRef.current?.scrollTo(0, 0);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  const isDark = theme === 'dark';
  const userName = dbProfile?.name || userProfile.name || 'Usuário';
  const userEmail = dbProfile?.email || userProfile.email || '';
  const userAvatar = dbProfile?.avatar_url || userProfile.avatar || '';

  // Setup global error tracking — re-registers when project changes
  useEffect(() => {
    if (!activeProjectId) return;
    const cleanup = setupGlobalErrorTracking(activeProjectId);
    return cleanup;
  }, [activeProjectId]);

  // If no project is active (e.g. direct URL access without selecting a project),
  // redirect to workspace selector — but only after a tick so the store can hydrate from localStorage.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!useStore.getState().activeProjectId) navigate('/workspaces', { replace: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [activeProjectId, navigate]);

  const handleLogout = async () => {
    await auditLog('logout', 'session');
    // Clear all application state before signing out
    useStore.getState().clearProjectCache();
    useStore.setState({ workspaces: [], teamMembers: [], activeWorkspaceId: '', activeProjectId: '', activeProjectType: 'crm' as any });
    // Clear React Query cache
    const { queryClient } = await import('@/App');
    queryClient.clear();
    // Clear persisted localStorage keys
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll, pushEnabled, enablePush } = useNotifications();

  // ⌘+1-5 navigation shortcuts + ⌘+K global search
  // Plus "n" — open New Lead dialog when on a CRM page that hosts it
  // (Pipeline / Leads). Bails when the user is already typing.
  // Plus "g + <letter>" chord — Vim/GitHub-style nav. Press g, then
  // within 1.5s press d/p/l/c/w to jump.
  useEffect(() => {
    const NAV_MAP: Record<string, string> = {
      '1': '/dashboard',
      '2': '/pipeline',
      '3': '/leads',
      '4': '/clientes',
      '5': '/configuracoes',
    };
    const chordDest = (key: string): string | null => {
      switch (key) {
        case 'd': return '/dashboard';
        case 'p': return '/pipeline';
        case 'l': return '/leads';
        case 'c': return '/clientes';
        case 'w': return '/workspaces';
        default: return null;
      }
    };
    let chordPending = false;
    let chordTimer: ReturnType<typeof setTimeout> | null = null;
    const clearChord = () => {
      chordPending = false;
      if (chordTimer) { clearTimeout(chordTimer); chordTimer = null; }
    };

    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && NAV_MAP[e.key]) {
        e.preventDefault();
        navigate(NAV_MAP[e.key]);
        return;
      }
      // Cmd+K is handled by CommandPalette component — removed duplicate handler

      const t = e.target as HTMLElement | null;
      const typing = !!t && (
        t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' || t.isContentEditable
      );
      if (typing) { clearChord(); return; }
      if (e.metaKey || e.ctrlKey || e.altKey) { clearChord(); return; }

      // Resolving the chord — second key after "g".
      if (chordPending) {
        const dest = chordDest(e.key);
        clearChord();
        if (dest) {
          e.preventDefault();
          // Stop other document-level listeners (e.g. Pipeline's "c"
          // handler) from also reacting to the chord's second key.
          e.stopImmediatePropagation();
          navigate(dest);
        }
        return;
      }

      // "g" (no shift) — start chord. We intentionally do NOT preventDefault
      // here so a quick double-tap leaks no harmful default. Chord expires
      // after 1.5s.
      if (e.key === 'g' && !e.shiftKey) {
        chordPending = true;
        chordTimer = setTimeout(clearChord, 1500);
        return;
      }

      // "n" — fire a global custom event that Pipeline/Leads listen for and
      // use to open the AddLeadDialog. We dispatch only when not typing.
      if (e.key === 'n' && !e.shiftKey) {
        // Only on CRM pages where the dialog is mounted; the listeners
        // themselves are no-ops elsewhere, but this avoids stray dispatches.
        const path = window.location.pathname;
        if (path.startsWith('/pipeline') || path.startsWith('/leads')) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('legacy:open-new-lead'));
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      clearChord();
    };
  }, [navigate]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      <CommandPalette />
      <KeyboardShortcutsModal />
      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

      <AppSidebar
        collapsed={collapsed}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isDark={isDark}
        userName={userName}
        userEmail={userEmail}
        userAvatar={userAvatar}
        activeProjectType={activeProjectType}
        handleLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <SkipLink />
        <AppTopBar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          isMobile={isMobile}
          setMobileOpen={setMobileOpen}
          userName={userName}
          userEmail={userEmail}
          userAvatar={userAvatar}
          handleLogout={handleLogout}
          notifications={notifications}
          unreadCount={unreadCount}
          markAsRead={markAsRead}
          markAllAsRead={markAllAsRead}
          clearAll={clearAll}
          pushEnabled={pushEnabled}
          enablePush={enablePush}
        />

        <div className="fixed top-3 right-4 z-40 pointer-events-none">
          <div className="pointer-events-auto"><SaveIndicator /></div>
        </div>
        <DbSync />
        <InsightNotifier />
        <Suspense fallback={null}>
          <OnboardingWizard context="crm" />
        </Suspense>

        <main id="main-content" ref={mainRef} className={cn("flex-1 overflow-auto p-3 md:p-6 scrollbar-thin", isMobile && "pb-20")} role="main">
          {syncProgress < 100 && activeProjectId ? (
            <div className="flex h-full min-h-[50vh] items-center justify-center">
              <div className="flex flex-col items-center gap-4 w-64">
                <Loader2 className="h-10 w-10 animate-spin text-secondary" />
                <div className="w-full space-y-2">
                  <Progress value={syncProgress} className="h-2" />
                  <p className="text-xs text-muted-foreground text-center">{syncProgress}% — Carregando dados...</p>
                </div>
              </div>
            </div>
          ) : (
          <Suspense fallback={
            <div className="flex h-full min-h-[50vh] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-secondary" />
            </div>
          }>
            {import.meta.env.VITE_ENABLE_BILLING === 'true' && location.pathname === '/dashboard' && <Suspense fallback={null}><PlanUsageBannerLazy /></Suspense>}
            <div
              key={location.pathname}
              className="animate-in fade-in duration-150"
            >
              <Outlet />
            </div>
          </Suspense>
          )}
        </main>

        {/* Mobile Bottom Navigation */}
        {isMobile && (
          <MobileBottomNav
            activeProjectType={activeProjectType}
            currentPath={location.pathname}
            onNavigate={(path) => navigate(path)}
            onMenuToggle={() => setMobileOpen(true)}
          />
        )}
      </div>
      <PWAInstallPrompt />
    </div>
  );
}

/**
 * Navegação inferior para dispositivos móveis.
 * Exibe itens diferentes conforme o tipo de projeto ativo (CRM, Tasks ou Funil).
 */
interface MobileBottomNavProps {
  activeProjectType: string;
  currentPath: string;
  onNavigate: (path: string) => void;
  onMenuToggle: () => void;
}

function MobileBottomNav({ currentPath, onNavigate, onMenuToggle }: MobileBottomNavProps) {
  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Kanban, label: 'Pipeline', path: '/pipeline' },
    { icon: Users, label: 'Leads', path: '/leads' },
    { icon: Activity, label: 'Atividades', path: '/atividades' },
    { icon: Menu, label: 'Menu', action: 'menu' as const },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border pwa-safe-bottom"
      style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-center justify-around px-2 pt-1.5">
        {navItems.map((item) => {
          const isActive = 'path' in item && currentPath === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              onClick={() => 'action' in item && item.action === 'menu' ? onMenuToggle() : 'path' in item && onNavigate(item.path!)}
              className={cn(
                "flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-[56px]",
                isActive ? "text-secondary" : "text-muted-foreground"
              )}
            >
              <Icon className={cn("h-5 w-5", isActive && "text-secondary")} />
              <span className={cn("text-[10px] font-medium", isActive && "text-secondary")}>{item.label}</span>
              {isActive && <div className="h-0.5 w-4 rounded-full bg-secondary mt-0.5" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default AppLayout;
